import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { Icon } from '../common/Icon';

// ─── ChatThread ────────────────────────────────────────────────────────────

export interface ChatThreadProps {
  children: ReactNode;
  /** When `true`, the thread auto-scrolls to the bottom whenever the
   * children list changes (new messages, typing indicators). Default
   * `true`. Set to `false` for "scroll to read history" surfaces. */
  autoScroll?: boolean;
}

/**
 * ChatThread — vertical scrollable container for a chat history.
 * Holds `ChatBubble`s and `ChatTimeMark`s. Owns the auto-scroll
 * behavior so individual bubbles don't have to know about scroll
 * containers.
 *
 * Sits inside a flex column with the composer pinned below it. Use
 * `<ChatTimeMark>` to break up long histories with date/time
 * separators (the WeChat pattern — instead of putting a timestamp
 * under every bubble, group bubbles under a centered "Saturday
 * 08:33" pill).
 *
 * @example
 * <div className="flex flex-col h-full">
 *   <ChatThread>
 *     <ChatTimeMark>Saturday 08:33</ChatTimeMark>
 *     <ChatBubble variant="theirs" senderAvatar={url}>...</ChatBubble>
 *     <ChatBubble variant="mine">...</ChatBubble>
 *     <ChatTimeMark>Saturday 12:09</ChatTimeMark>
 *     <ChatBubble variant="mine">https://easynet.run</ChatBubble>
 *   </ChatThread>
 *   <ChatComposer onSend={send} ... />
 * </div>
 */
export function ChatThread({ children, autoScroll = true }: ChatThreadProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!autoScroll || !ref.current) return;
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.scrollTop = ref.current.scrollHeight;
      }
    });
  }, [autoScroll, children]);

  return (
    <div
      ref={ref}
      className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-2"
    >
      {children}
    </div>
  );
}

// ─── ChatTimeMark ──────────────────────────────────────────────────────────

/**
 * Centered date/time separator that breaks up a chat history. WeChat
 * uses these to group messages by time chunks instead of putting a
 * timestamp under every bubble — much less visual noise.
 *
 * @example
 * <ChatTimeMark>Saturday 08:33</ChatTimeMark>
 */
export function ChatTimeMark({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-center py-2">
      <span className="text-[11px] text-gray-400 dark:text-gray-500">
        {children}
      </span>
    </div>
  );
}

// ─── ChatBubble ────────────────────────────────────────────────────────────

export interface ChatBubbleProps {
  /** Whose side the bubble is on. `mine` aligns right with primary
   * fill; `theirs` aligns left with neutral fill; `system` is a
   * centered, low-key info pill. */
  variant: 'mine' | 'theirs' | 'system';
  /** The body text or any custom node. */
  children: ReactNode;
  /** Sender display name — only shown for `theirs` bubbles in group
   * threads. Pass `undefined` for direct messages. */
  senderName?: ReactNode;
  /** Avatar URL — shown as a 36×36 rounded-square next to the
   * bubble (NOT circle, WeChat-style). Works for both `mine` and
   * `theirs`. Omit to hide the avatar slot entirely. */
  senderAvatar?: string;
}

/**
 * ChatBubble — a single message bubble in WeChat style.
 *
 * Visual rules (matched against actual WeChat iOS):
 *   - Avatar is **36×36 rounded square** (not circle), tightly
 *     pressed against the bubble's edge with no gap
 *   - `mine` bubbles use the brand primary color, right-aligned,
 *     with the avatar on the right side
 *   - `theirs` bubbles use a neutral fill, left-aligned, avatar
 *     on the left
 *   - The bubble has a **directional tail** (small triangle nub
 *     at the corner closest to the avatar) — implemented with a
 *     CSS clip-path-free rounded-corner mask
 *   - Long content wraps; the bubble caps at 75% of the thread width
 *   - Timestamps are NOT shown under each bubble — use
 *     `<ChatTimeMark>` separators between time groups instead
 *   - Group `theirs` bubbles show the sender name above the bubble
 *
 * @example
 * <ChatBubble variant="mine">
 *   https://easynet.run
 * </ChatBubble>
 *
 * <ChatBubble variant="theirs" senderName="Sage" senderAvatar={url}>
 *   早。AAAI 2026 的阅读清单整完了吗？
 * </ChatBubble>
 *
 * <ChatBubble variant="system">Sage joined the conversation</ChatBubble>
 */
export function ChatBubble({
  variant,
  children,
  senderName,
  senderAvatar,
}: ChatBubbleProps) {
  if (variant === 'system') {
    return (
      <div className="flex justify-center py-1">
        <span className="text-[11px] text-gray-400 dark:text-gray-500 px-3">
          {children}
        </span>
      </div>
    );
  }

  const isMine = variant === 'mine';

  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`flex items-start gap-2 max-w-[78%] ${isMine ? 'flex-row-reverse' : 'flex-row'}`}
      >
        {/* Avatar slot — 36×36 rounded square (the WeChat way),
            not a circle. Renders only when an avatar URL was
            actually provided; no empty placeholder. Both `mine`
            and `theirs` can carry an avatar — pass `senderAvatar`
            for either side. */}
        {senderAvatar && (
          <div className="flex-shrink-0 w-9 h-9 rounded-md overflow-hidden bg-gray-200 dark:bg-white/[0.08]">
            <img src={senderAvatar} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="min-w-0 flex flex-col">
          {/* Group sender name (only for theirs bubbles in groups) */}
          {!isMine && senderName && (
            <span className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 ml-1">
              {senderName}
            </span>
          )}

          {/* The bubble itself.
              The directional tail is a small absolutely-positioned
              square rotated 45° at the avatar-side top corner. */}
          <div
            className={`
              relative px-3 py-2 text-[15px] leading-snug whitespace-pre-wrap break-words rounded
              ${isMine
                ? 'bg-primary text-white'
                : 'bg-gray-100 dark:bg-white/[0.06] text-gray-900 dark:text-gray-100'}
            `}
          >
            {/* Tail nub */}
            <span
              aria-hidden
              className={`
                absolute top-2 w-2 h-2 rotate-45
                ${isMine
                  ? '-right-[3px] bg-primary'
                  : '-left-[3px] bg-gray-100 dark:bg-white/[0.06]'}
              `}
            />
            <span className="relative">{children}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Mention highlight renderer ────────────────────────────────────────────

/**
 * Splits a chat composer text into spans, painting `@Name` segments
 * in the primary brand color when the name matches a known mention
 * candidate. Used by the overlay div that sits behind the transparent
 * textarea to give the impression of inline-styled mentions.
 *
 * The matching logic is greedy + case-sensitive: for each `@`, we
 * find the candidate whose `name` is the longest prefix of what
 * follows. This handles both single-word names ("Sage") and names
 * with spaces ("Dr. Lin"), and avoids accidentally highlighting
 * `@example.com` as a mention.
 */
function renderMentionHighlight(text: string, candidates: MentionCandidate[]): ReactNode {
  if (text.length === 0) {
    // Render a zero-width space so the overlay box still has a line
    // height — keeps the textarea baseline aligned even when empty.
    return '\u200B';
  }
  if (candidates.length === 0) return text;

  // Sort by name length descending so longer matches win.
  const sorted = [...candidates].sort((a, b) => b.name.length - a.name.length);

  const out: ReactNode[] = [];
  let i = 0;
  let bufferStart = 0;

  const flushBuffer = (until: number) => {
    if (until > bufferStart) {
      out.push(text.slice(bufferStart, until));
    }
  };

  while (i < text.length) {
    if (text[i] === '@') {
      // `@` must be at start or preceded by whitespace
      const prev = i === 0 ? ' ' : text[i - 1];
      if (/\s/.test(prev) || i === 0) {
        const after = text.slice(i + 1);
        const hit = sorted.find((c) => after.startsWith(c.name));
        if (hit) {
          flushBuffer(i);
          out.push(
            <span key={`m-${i}`} className="text-primary font-medium">
              @{hit.name}
            </span>,
          );
          i += 1 + hit.name.length;
          bufferStart = i;
          continue;
        }
      }
    }
    i++;
  }
  flushBuffer(text.length);
  return out;
}

// ─── ChatComposer ──────────────────────────────────────────────────────────

/** A single action shown in the WeChat-style attach panel that
 * expands below the composer when the user taps `+`. Photo /
 * camera / file / location / etc. The composer doesn't actually
 * pick or upload anything — it just renders the grid and fires the
 * action's `onClick`. */
export interface AttachAction {
  /** Material Symbols icon name shown in the soft primary tile. */
  icon: string;
  /** Short label rendered under the tile. */
  label: string;
  onClick: () => void;
}

/** A user, agent, or anything else that can be @-mentioned. The
 * composer doesn't care what `id` represents — it just round-trips
 * the ids of the picked candidates back via `onSend`'s second arg. */
export interface MentionCandidate {
  id: string;
  /** Display name shown both in the popup list and in the inserted
   * mention chip in the input (`@Name`). */
  name: string;
  /** Optional avatar URL shown in the popup list. */
  avatar?: string;
  /** Optional one-line subtitle in the popup list (role, handle). */
  subtitle?: string;
}

export interface ChatComposerProps {
  /** Called when the user submits text. The second argument is the
   * list of mention candidate ids that appear inline in `text` as
   * `@Name`. */
  onSend: (text: string, mentions: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Label for the Send button that replaces `+` when text is
   * non-empty. Defaults to `"Send"` (English). Pass an i18n string
   * to localize. */
  sendLabel?: string;
  /** Simple handler for the `+` button. Use this for "open my own
   * external picker" cases. Mutually exclusive with `attachActions` —
   * if both are provided, `attachActions` wins and `onAttach` is
   * ignored. When neither is provided, the `+` slot is hidden in
   * empty state. */
  onAttach?: () => void;
  /** WeChat-style attach panel actions. When provided, tapping `+`
   * expands a grid of these actions inline above the composer (the
   * iOS WeChat pattern: 4 columns of icon tiles). Tapping `+` again
   * collapses the panel. Sending a message also collapses it. */
  attachActions?: AttachAction[];
  /** Optional handler for the emoji button. When omitted the
   * button is hidden. */
  onEmoji?: () => void;
  /** Optional voice handlers. When provided, the composer exposes
   * the WeChat-style state machine: a left-side voice toggle that
   * swaps the textarea for a press-and-hold "Hold to talk" button.
   * The actual recording API is the caller's job — this primitive
   * only emits start/stop/cancel events. */
  voice?: {
    onStart: () => void;
    onStop: () => void;
    onCancel: () => void;
  };
  /** Optional list of mention candidates. When provided, typing `@`
   * triggers a popup of matching candidates. Picking one inserts
   * `@Name ` at the cursor and the candidate's id is added to the
   * mentions array passed to `onSend`. Pass `undefined` to disable
   * the @-mention feature entirely. */
  mentionable?: MentionCandidate[];
  /** Optional pending attachments preview rendered above the input. */
  attachments?: ReactNode;
}

/**
 * ChatComposer — the bottom input bar, modeled on WeChat iOS.
 *
 * Layout (left → right):
 *
 *     ┌───┬───────────────────────────┬───┬─────┐
 *     │ ⏵ │ text input (or hold-to-talk) │😀│ + / Send │
 *     └───┴───────────────────────────┴───┴─────┘
 *
 *   - **Voice toggle (left)**: switches the central area between
 *     text input and a press-and-hold record button.
 *   - **Center**: text textarea OR hold-to-talk button.
 *   - **Emoji (right-1)**: opens emoji panel.
 *   - **Trailing slot (right-2)**: shows `+` when the input is empty
 *     (calls `onAttach`); morphs into a green Send button when the
 *     input has text. WeChat does this exact swap.
 *
 * Keyboard:
 *   - **Enter** = newline (so the user can compose multi-line
 *     messages naturally)
 *   - **Shift+Enter** = send
 *   This is the inverse of the typical chat default; the user wanted
 *   it this way and it matches WeChat desktop behavior.
 *
 * Mentions:
 *   - Pass `mentionable` to enable `@`-mentions. Typing `@` opens a
 *     popup above the input filtered live by what the user types.
 *     Up/Down arrows navigate, Enter (or click) inserts `@Name `,
 *     Escape closes the popup.
 *   - The chosen ids are passed back via `onSend(text, mentions)`.
 *
 * @example
 * <ChatComposer
 *   onSend={(text, mentions) => sendMessage(text, mentions)}
 *   onAttach={() => openAttachPanel()}
 *   onEmoji={() => openEmojiPanel()}
 *   mentionable={agents.map(a => ({ id: a.id, name: a.name, avatar: a.avatar }))}
 *   voice={{
 *     onStart:  () => recorder.start(),
 *     onStop:   () => recorder.stopAndSend(),
 *     onCancel: () => recorder.discard(),
 *   }}
 * />
 */
export function ChatComposer({
  onSend,
  placeholder = 'Type a message…',
  disabled,
  sendLabel = 'Send',
  onAttach,
  attachActions,
  onEmoji,
  voice,
  mentionable,
  attachments,
}: ChatComposerProps) {
  const [text, setText] = useState('');
  const [voiceMode, setVoiceMode] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [recording, setRecording] = useState<null | { willCancel: boolean }>(null);
  const [elapsed, setElapsed] = useState(0);
  const elapsedTimer = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // ─── @-mention state ────────────────────────────────────────────
  // When the user types `@`, we track the cursor position of that
  // `@` and the query string typed after it. The popup renders
  // filtered candidates and the user picks one with arrow + enter
  // or by clicking. Picking inserts `@Name ` and clears the popup.
  const [mention, setMention] = useState<{
    /** Index in `text` where the triggering `@` lives. */
    anchor: number;
    /** Live query (text after `@`, before cursor). */
    query: string;
    /** Currently highlighted candidate index in the filtered list. */
    highlight: number;
  } | null>(null);

  /** Track which mention ids the user has actually inserted. We
   * round-trip the full set on send. */
  const insertedMentions = useRef<Set<string>>(new Set());

  // Recompute mention state whenever the input changes. The trigger
  // logic: find the most recent `@` before the cursor that is at the
  // start of the string or preceded by whitespace, and that doesn't
  // contain whitespace between itself and the cursor.
  const recomputeMention = useCallback(
    (value: string, caret: number) => {
      if (!mentionable || mentionable.length === 0) {
        setMention(null);
        return;
      }
      // Walk backwards from caret looking for `@`
      let i = caret - 1;
      while (i >= 0) {
        const ch = value[i];
        if (ch === '@') {
          // The char before `@` must be start-of-string or whitespace
          const prev = i === 0 ? ' ' : value[i - 1];
          if (/\s/.test(prev) || i === 0) {
            const query = value.slice(i + 1, caret);
            // Cancel popup as soon as the query contains whitespace
            // (the user moved past the mention).
            if (/\s/.test(query)) {
              setMention(null);
              return;
            }
            setMention((cur) => ({
              anchor: i,
              query,
              highlight: cur && cur.anchor === i ? cur.highlight : 0,
            }));
            return;
          }
          setMention(null);
          return;
        }
        if (/\s/.test(ch)) break;
        i--;
      }
      setMention(null);
    },
    [mentionable],
  );

  const filteredCandidates = useMemo(() => {
    if (!mention || !mentionable) return [];
    const q = mention.query.toLowerCase();
    return mentionable.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [mention, mentionable]);

  /** Insert `@Name ` at the mention anchor, replacing whatever the
   * user had typed after the `@`. Also records the mention id and
   * moves the caret to just after the inserted text. */
  const pickMention = useCallback(
    (candidate: MentionCandidate) => {
      if (!mention || !textareaRef.current) return;
      const before = text.slice(0, mention.anchor);
      const after = text.slice(mention.anchor + 1 + mention.query.length);
      const inserted = `@${candidate.name} `;
      const next = before + inserted + after;
      const newCaret = (before + inserted).length;
      setText(next);
      setMention(null);
      insertedMentions.current.add(candidate.id);
      // Restore caret on next tick (after textarea re-renders)
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          ta.setSelectionRange(newCaret, newCaret);
        }
      });
    },
    [mention, text],
  );

  // Auto-grow textarea up to ~5 lines
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [text]);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Filter mentions to only those whose `@Name` actually still
    // appears in the final text — the user may have deleted them.
    const stillPresent = mentionable
      ? mentionable
          .filter((c) => insertedMentions.current.has(c.id))
          .filter((c) => trimmed.includes(`@${c.name}`))
          .map((c) => c.id)
      : [];
    onSend(trimmed, stillPresent);
    setText('');
    insertedMentions.current.clear();
    setMention(null);
    setAttachOpen(false);
  }, [text, mentionable, onSend]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setText(value);
      recomputeMention(value, e.target.selectionStart ?? value.length);
    },
    [recomputeMention],
  );

  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const ta = e.currentTarget;
      recomputeMention(ta.value, ta.selectionStart ?? ta.value.length);
    },
    [recomputeMention],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Mention popup keyboard navigation takes priority
      if (mention && filteredCandidates.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMention((m) => m && { ...m, highlight: (m.highlight + 1) % filteredCandidates.length });
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMention((m) => m && { ...m, highlight: (m.highlight - 1 + filteredCandidates.length) % filteredCandidates.length });
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          pickMention(filteredCandidates[mention.highlight]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMention(null);
          return;
        }
      }
      // Send shortcut: Shift+Enter (Enter alone is a newline). This
      // is the inverse of the conventional chat default — the user
      // wanted it this way and it matches WeChat desktop.
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [mention, filteredCandidates, handleSend, pickMention],
  );

  // Voice press-and-hold lifecycle
  const startRecording = useCallback(() => {
    if (!voice) return;
    voice.onStart();
    setRecording({ willCancel: false });
    setElapsed(0);
    elapsedTimer.current = window.setInterval(() => {
      setElapsed((n) => n + 1);
    }, 1000);
  }, [voice]);

  const stopRecording = useCallback(
    (cancelled: boolean) => {
      if (!voice) return;
      if (elapsedTimer.current != null) {
        window.clearInterval(elapsedTimer.current);
        elapsedTimer.current = null;
      }
      if (cancelled) voice.onCancel();
      else voice.onStop();
      setRecording(null);
      setElapsed(0);
    },
    [voice],
  );

  useEffect(() => {
    return () => {
      if (elapsedTimer.current != null) window.clearInterval(elapsedTimer.current);
    };
  }, []);

  // Hide the voice toggle while the user is typing — their attention
  // is on the input, voice would just clutter the row. WeChat does
  // this same trick.
  const showVoiceToggle = !text.trim();

  return (
    <div
      className="
        flex-shrink-0 border-t border-gray-100 dark:border-white/[0.06]
        bg-white dark:bg-[#0B0B0D]
      "
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {attachments && <div className="px-3 pt-2">{attachments}</div>}

      <div className="flex items-center gap-2 px-2 py-1.5">
        {/* Voice toggle (leftmost) — hides when text is non-empty */}
        {voice && showVoiceToggle && (
          <ComposerIconButton
            icon={voiceMode ? 'keyboard' : 'graphic_eq'}
            label={voiceMode ? 'Switch to keyboard' : 'Switch to voice'}
            onClick={() => setVoiceMode((v) => !v)}
            disabled={disabled}
          />
        )}

        {/* Center: textarea or hold-to-talk button */}
        {voiceMode && voice ? (
          <button
            type="button"
            onPointerDown={startRecording}
            onPointerUp={() => stopRecording(recording?.willCancel ?? false)}
            onPointerLeave={() => {
              if (recording) setRecording({ willCancel: true });
            }}
            onPointerEnter={() => {
              if (recording) setRecording({ willCancel: false });
            }}
            disabled={disabled}
            className={`
              flex-1 h-9 rounded text-[15px] font-medium select-none
              transition-colors
              ${recording
                ? recording.willCancel
                  ? 'bg-red-500/10 text-red-500 border border-red-500/30'
                  : 'bg-primary/10 text-primary border border-primary/30'
                : 'bg-gray-100 dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 border border-transparent'}
            `}
          >
            {recording
              ? recording.willCancel
                ? 'Release to cancel'
                : `Recording… ${elapsed}s`
              : 'Hold to talk'}
          </button>
        ) : (
          /* Wrapper owns the visible bg + rounded shape so the
             overlay (highlighted text) and the textarea (caret +
             input handling, transparent text) can stack without
             one painting over the other. */
          <div className="
            flex-1 relative rounded
            bg-gray-100 dark:bg-white/[0.06]
            focus-within:ring-1 focus-within:ring-primary/40
          ">
            {/* Highlight overlay — mirrors the textarea content but
                renders `@Name` segments in primary color + medium
                weight. Sits below the textarea in DOM order, but
                because the textarea is `bg-transparent` the overlay
                is visually on top of the bg. */}
            <div
              aria-hidden
              className="
                absolute inset-0 pointer-events-none
                px-3 py-[7px] text-[15px] leading-[1.4]
                whitespace-pre-wrap break-words
                text-gray-900 dark:text-gray-100
                overflow-hidden
              "
            >
              {renderMentionHighlight(text, mentionable ?? [])}
            </div>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleChange}
              onSelect={handleSelect}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                // Defer so click on popup item still fires before blur
                // tears the popup down.
                setTimeout(() => setMention(null), 150);
              }}
              placeholder={placeholder}
              disabled={disabled}
              rows={1}
              className="
                relative block w-full resize-none bg-transparent
                px-3 py-[7px] text-[15px] leading-[1.4]
                text-transparent caret-gray-900 dark:caret-gray-100
                placeholder:text-gray-400 dark:placeholder:text-gray-500
                focus:outline-none
                disabled:opacity-50
              "
              style={{ minHeight: '36px', maxHeight: '120px' }}
            />
            {/* Mention popup — anchored to the textarea bottom so it
                floats above the composer (toward the chat history). */}
            {mention && filteredCandidates.length > 0 && (
              <div className="
                absolute left-0 right-0 bottom-full mb-2
                rounded shadow-lg
                bg-white dark:bg-[#171719]
                border border-gray-100 dark:border-white/[0.08]
                overflow-hidden
                z-50
              ">
                {filteredCandidates.map((c, i) => {
                  const active = i === mention.highlight;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      // `mousedown` fires before the textarea's `blur`,
                      // so we can pick the candidate before the popup
                      // gets torn down.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickMention(c);
                      }}
                      onMouseEnter={() => setMention((m) => m && { ...m, highlight: i })}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 text-left
                        ${active
                          ? 'bg-primary/10 text-primary'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04]'}
                      `}
                    >
                      {c.avatar ? (
                        <img src={c.avatar} alt="" className="flex-shrink-0 w-7 h-7 rounded-md object-cover" />
                      ) : (
                        <div className="flex-shrink-0 w-7 h-7 rounded-md bg-gradient-to-br from-primary/25 to-primary/5 flex items-center justify-center">
                          <span className="text-[11px] font-bold text-primary">{c.name[0]?.toUpperCase()}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        {c.subtitle && (
                          <div className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{c.subtitle}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Emoji — always present in keyboard mode */}
        {onEmoji && !voiceMode && (
          <ComposerIconButton
            icon="mood"
            label="Emoji"
            onClick={onEmoji}
            disabled={disabled}
          />
        )}

        {/* Trailing slot — swaps between `+` (empty input) and a
            green Send pill (non-empty input). The two share the same
            position so the user's eye doesn't have to move when text
            appears. WeChat does exactly this.

            When `attachActions` is provided, `+` toggles an inline
            attach panel below the composer (4-col icon grid). When
            only `onAttach` is provided, it fires a callback for the
            consumer's own picker. */}
        {!voiceMode && (
          text.trim() ? (
            <button
              type="button"
              onClick={handleSend}
              disabled={disabled}
              className="
                flex-shrink-0 h-9 px-4 rounded-md
                bg-primary text-white text-[15px] font-medium
                hover:bg-primary/90 active:scale-[0.98]
                disabled:opacity-40 disabled:cursor-not-allowed
                transition-all
              "
            >
              {sendLabel}
            </button>
          ) : (
            (attachActions && attachActions.length > 0) ? (
              <ComposerIconButton
                icon={attachOpen ? 'close' : 'add_circle_outline'}
                label="Attach"
                onClick={() => setAttachOpen((o) => !o)}
                disabled={disabled}
              />
            ) : (
              onAttach && (
                <ComposerIconButton
                  icon="add_circle_outline"
                  label="Attach"
                  onClick={onAttach}
                  disabled={disabled}
                />
              )
            )
          )
        )}
      </div>

      {/* Inline attach panel — WeChat-style 4-col icon grid that
          slides down below the composer when `+` is tapped. */}
      {attachOpen && attachActions && attachActions.length > 0 && (
        <div className="border-t border-gray-100 dark:border-white/[0.06] px-4 py-4">
          <div className="grid grid-cols-4 gap-4">
            {attachActions.map((action, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  action.onClick();
                  setAttachOpen(false);
                }}
                className="flex flex-col items-center gap-1.5 group"
              >
                <div className="
                  w-14 h-14 rounded flex items-center justify-center
                  bg-gray-100 dark:bg-white/[0.05]
                  text-gray-600 dark:text-gray-300
                  group-hover:bg-primary/10 group-hover:text-primary
                  group-active:scale-95
                  transition-all
                ">
                  <Icon name={action.icon} size={26} />
                </div>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ComposerIconButton ────────────────────────────────────────────────────

/**
 * Internal — the round 36×36 icon buttons that flank the composer.
 * Slightly larger touch target than the main `<IconButton>` so the
 * row reads as one continuous control strip.
 */
function ComposerIconButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="
        flex-shrink-0 w-9 h-9 flex items-center justify-center
        text-gray-600 dark:text-gray-300
        rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.06]
        disabled:opacity-40 transition-colors
      "
    >
      <Icon name={icon} size={24} />
    </button>
  );
}
